/**
 * Toast — minimal, dependency-free notification system used across the app
 * to replace native `alert()` (which steals focus, looks alien, breaks
 * iPad workflows, and can't carry rich content like an "Undo" button).
 *
 * Surface area:
 *   <ToastProvider>...app...</ToastProvider>   // mount once at the app root
 *   const { push } = useToast();
 *   push({ kind: 'success', title: 'Saved.', body: 'Synced to server.' });
 *   push({ kind: 'error',   title: 'Submit failed', body: String(err) });
 *
 * Kinds map to StatusPill semantics so the visual vocabulary is consistent
 * with the rest of the product (compliant / observation / non-compliant /
 * reference).
 *
 * Toasts auto-dismiss after `durationMs` (default 4500 ms, errors 7500 ms).
 * They stack bottom-right on desktop, full-width sheet on mobile, and
 * never block keyboard focus.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CheckCircle2, AlertTriangle, AlertOctagon, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

export interface ToastInput {
  kind?: ToastKind;
  title: string;
  body?: string;
  /** Optional action button. When present, the toast stays sticky until
   *  dismissed or the action is invoked. */
  action?: { label: string; onClick: () => void };
  /** Override auto-dismiss timer in ms. Pass 0 for sticky. */
  durationMs?: number;
}

interface ToastEntry extends Required<Pick<ToastInput, 'kind' | 'title'>> {
  id: number;
  body?: string;
  action?: ToastInput['action'];
  durationMs: number;
  createdAt: number;
}

interface ToastContextValue {
  push: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const KIND_DEFAULTS = {
  success: { icon: CheckCircle2,  cls: 'bg-emerald-900/80 border-emerald-500/50 text-emerald-50',  iconCls: 'text-emerald-300',  ms: 4500 },
  warning: { icon: AlertTriangle, cls: 'bg-amber-900/80   border-amber-500/50   text-amber-50',    iconCls: 'text-amber-300',    ms: 6000 },
  error:   { icon: AlertOctagon,  cls: 'bg-red-900/80     border-red-500/50     text-red-50',      iconCls: 'text-red-300',      ms: 7500 },
  info:    { icon: Info,          cls: 'bg-slate-900/90   border-slate-700      text-slate-100',   iconCls: 'text-cyan-300',     ms: 4500 },
} as const;

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((t: ToastInput): number => {
    const kind = t.kind ?? 'info';
    const ms = t.durationMs ?? KIND_DEFAULTS[kind].ms;
    const id = nextId++;
    const entry: ToastEntry = {
      id,
      kind,
      title: t.title,
      body: t.body,
      action: t.action,
      durationMs: ms,
      createdAt: Date.now(),
    };
    setItems((prev) => [...prev, entry]);
    if (ms > 0 && !t.action) {
      const handle = window.setTimeout(() => dismiss(id), ms);
      timers.current.set(id, handle);
    }
    return id;
  }, [dismiss]);

  // Cleanup pending timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((h) => clearTimeout(h));
      map.clear();
    };
  }, []);

  const ctx = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Soft fallback: log + alert if the provider isn't mounted (eg.
    // someone calls useToast outside the tree). Better than throwing
    // and crashing the page.
    return {
      push: (t) => { console.warn('Toast outside ToastProvider:', t); return -1; },
      dismiss: () => undefined,
    };
  }
  return ctx;
}

/* ─── Viewport ────────────────────────────────────────────────────── */

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div
      className="fixed z-50 pointer-events-none flex flex-col gap-2 px-4 sm:px-0 inset-x-0 bottom-4 sm:inset-auto sm:bottom-5 sm:right-5 sm:max-w-sm"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <ToastCard key={t.id} t={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastCard({
  t,
  onDismiss,
}: {
  t: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const meta = KIND_DEFAULTS[t.kind];
  const Icon = meta.icon;
  return (
    <div
      role={t.kind === 'error' ? 'alert' : 'status'}
      aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto rounded-xl border backdrop-blur-md shadow-2xl px-4 py-3 flex items-start gap-3 animate-[toast-in_220ms_cubic-bezier(0.2,0.8,0.2,1)] ${meta.cls}`}
      style={{ boxShadow: '0 16px 40px -16px rgba(0,0,0,0.6)' }}
    >
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${meta.iconCls}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-snug">{t.title}</div>
        {t.body && <div className="text-xs opacity-90 mt-0.5 leading-relaxed">{t.body}</div>}
        {t.action && (
          <button
            onClick={() => { t.action?.onClick(); onDismiss(t.id); }}
            className="mt-2 inline-flex items-center text-xs font-bold uppercase tracking-wider hover:underline"
          >
            {t.action.label} →
          </button>
        )}
      </div>
      <button
        onClick={() => onDismiss(t.id)}
        aria-label="Dismiss"
        className="text-current opacity-60 hover:opacity-100 shrink-0 transition-opacity"
      >
        <X className="w-4 h-4" />
      </button>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>
  );
}
